import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccess, useSession } from '@/lib/session';
import { PLAN_LABEL } from '@/lib/access';
import { signOut } from '@/lib/auth';
import { fetchOrders, subscribeOrders, type Order } from '@/lib/data';
import { registerPush, unregisterPush } from '@/lib/push';
import { useDashboardDate, DATE_RANGE_OPTIONS, type DateRangeLabel } from '@/lib/dashboardDate';

function timeAgo(iso: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `${hrs}h ago` : `${Math.floor(hrs / 24)}d ago`;
}

export default function TopBar() {
  const navigate = useNavigate();
  const { user, restaurant } = useSession();
  const access = useAccess();
  const { label: dateRange, setLabel: setDateRange, customFrom, customTo, setCustomFrom, setCustomTo } = useDashboardDate();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Live recent-orders feed for the notification bell.
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [lastSeen, setLastSeen] = useState<number>(() => Date.now());
  const seededRef = useRef(false);
  // FCM web-push token for this admin browser (so a backgrounded tab still
  // gets new-order pushes). Removed on sign-out.
  const pushTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!restaurant.id) return;
    let active = true;
    const load = async () => {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const orders = await fetchOrders(restaurant.id, {
          from: today.toISOString(),
          limit: 15,
        });
        if (!active) return;
        // First load seeds "seen" so we don't flag pre-existing orders as new.
        if (!seededRef.current) {
          seededRef.current = true;
          setLastSeen(Date.now());
        }
        setRecentOrders(orders);
      } catch { /* keep prior list on error */ }
    };
    load();
    const unsub = subscribeOrders(restaurant.id, load);
    return () => { active = false; unsub(); };
  }, [restaurant.id]);

  // Register this admin browser for background web push (best-effort, opt-in via
  // the browser permission prompt). A foreground push just refreshes the bell
  // feed — the realtime subscription above already covers open tabs, so we
  // don't raise a second alert.
  useEffect(() => {
    if (!restaurant.id) return;
    let active = true;
    registerPush('admin', restaurant.id, () => {
      // foreground message: nudge the recent-orders feed (de-duped by the bell)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      fetchOrders(restaurant.id, { from: today.toISOString(), limit: 15 })
        .then((orders) => { if (active) setRecentOrders(orders); })
        .catch(() => { /* keep prior list */ });
    }).then((token) => {
      if (active) pushTokenRef.current = token;
    });
    return () => { active = false; };
  }, [restaurant.id]);

  const unreadCount = recentOrders.filter(
    (o) => new Date(o.placedAt).getTime() > lastSeen,
  ).length;

  const openNotifications = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    if (next) setLastSeen(Date.now()); // opening marks them read
  };

  const publicUrl = `${window.location.origin}/${restaurant.slug}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const dateOptions = DATE_RANGE_OPTIONS;

  const displayName = user.displayName || user.phoneNumber || user.email || 'Account';
  const initials = (user.displayName ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const locationLine = restaurant.address ?? restaurant.slug;

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await unregisterPush(pushTokenRef.current);
      pushTokenRef.current = null;
      await signOut();
      navigate('/login');
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <header className="h-16 flex items-center justify-between px-6 bg-background-50 border-b border-background-200 sticky top-0 z-30">
      {/* Left — brand + outlet + date */}
      <div className="flex items-center gap-4">
        {/* Brand */}
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center shrink-0">
            <i className="ri-restaurant-line text-background-50 text-base"></i>
          </div>
          <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">{restaurant.name}</span>
        </div>

        <div className="w-px h-5 bg-background-200"></div>

        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-secondary-400"></div>
          <span className="text-xs text-foreground-400 truncate max-w-[16rem]">{locationLine}</span>
        </div>

        {/* Public restaurant link */}
        <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-background-100 border border-background-200">
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary-600 hover:text-primary-700 hover:underline truncate max-w-[14rem]"
            title={publicUrl}
          >
            {window.location.host}/{restaurant.slug}
          </a>
          <button
            onClick={copyLink}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-background-200 transition-colors cursor-pointer shrink-0"
            title="Copy link"
          >
            <i className={`${linkCopied ? 'ri-check-line text-green-600' : 'ri-file-copy-line text-foreground-400'} text-xs`}></i>
          </button>
        </div>

        {/* Date range */}
        <div className="relative">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line text-sm"></i>
            </div>
            <span className="whitespace-nowrap">
              {dateRange === 'Custom' && customFrom && customTo ? `${customFrom} → ${customTo}` : dateRange}
            </span>
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-down-s-line text-xs"></i>
            </div>
          </button>

          {showDatePicker && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowDatePicker(false)}></div>
              <div className="absolute top-full mt-1 left-0 w-52 bg-background-50 border border-background-200 rounded-lg shadow-sm z-50 py-1">
                {dateOptions.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => { setDateRange(opt as DateRangeLabel); if (opt !== 'Custom') setShowDatePicker(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap ${
                      dateRange === opt ? 'text-primary-600 font-medium bg-primary-50' : 'text-foreground-600'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
                {dateRange === 'Custom' && (
                  <div className="px-3 py-2 border-t border-background-100 mt-1 space-y-2">
                    <label className="block">
                      <span className="text-[11px] font-medium text-foreground-500">From</span>
                      <input type="date" value={customFrom} max={customTo || undefined}
                        onChange={(e) => setCustomFrom(e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 text-xs border border-background-200 rounded-md bg-background-50" />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-medium text-foreground-500">To</span>
                      <input type="date" value={customTo} min={customFrom || undefined}
                        onChange={(e) => setCustomTo(e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 text-xs border border-background-200 rounded-md bg-background-50" />
                    </label>
                    <button onClick={() => setShowDatePicker(false)}
                      className="w-full px-3 py-1.5 text-xs font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md cursor-pointer">
                      Apply
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right — trial badge, search, notifications, profile */}
      <div className="flex items-center gap-3">
        {/* Trial / plan badge */}
        {access.status === 'trialing' && (
          <button
            onClick={() => navigate('/dashboard/billing')}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-50 border border-accent-200 text-accent-700 text-xs font-medium hover:bg-accent-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-time-line text-sm"></i>
            <span>Free trial · {access.daysLeft} {access.daysLeft === 1 ? 'day' : 'days'} left</span>
            <span className="font-semibold underline">Upgrade</span>
          </button>
        )}
        {access.status === 'active' && access.planTier && (
          <button
            onClick={() => navigate('/dashboard/billing')}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-50 border border-primary-200 text-primary-700 text-xs font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-vip-crown-line text-sm"></i>
            <span>{PLAN_LABEL[access.planTier]} plan</span>
          </button>
        )}

        {/* Search */}
        <div className="relative">
          <div className="w-4 h-4 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
            <i className="ri-search-line text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Search orders, items..."
            className="pl-9 pr-3 py-1.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-700 placeholder-foreground-400 w-56 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
          />
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={openNotifications}
            className="relative w-9 h-9 rounded-lg flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-notification-3-line text-lg text-foreground-500"></i>
            </div>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-accent-500 text-background-50 text-[10px] font-bold flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)}></div>
              <div className="absolute top-full right-0 mt-2 w-80 bg-background-50 border border-background-200 rounded-lg shadow-sm z-50">
                <div className="flex items-center justify-between px-4 py-3 border-b border-background-200">
                  <span className="text-sm font-semibold text-foreground-800">Notifications</span>
                  <button
                    onClick={() => { setShowNotifications(false); navigate('/dashboard/orders'); }}
                    className="text-xs text-primary-600 hover:underline cursor-pointer"
                  >
                    View all
                  </button>
                </div>
                {recentOrders.length === 0 ? (
                  <div className="px-4 py-8 flex flex-col items-center gap-2 text-center">
                    <i className="ri-notification-off-line text-xl text-foreground-300"></i>
                    <p className="text-sm text-foreground-400">No notifications yet</p>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto py-1">
                    {recentOrders.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => { setShowNotifications(false); navigate('/dashboard/orders'); }}
                        className="w-full text-left px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-3"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                          <i className="ri-receipt-line text-primary-600 text-sm"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground-800 truncate">
                            <span className="font-medium">{o.type || 'Order'}</span>
                            {o.tableId ? ` · Table ${o.tableId}` : o.customer ? ` · ${o.customer}` : ''}
                          </p>
                          <p className="text-xs text-foreground-400">{o.status} · {timeAgo(o.placedAt)}</p>
                        </div>
                        <span className="text-sm font-semibold text-foreground-700 shrink-0">₹{Math.round(o.total)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Profile */}
        <div className="relative">
          <button
            onClick={() => setShowProfile(!showProfile)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-secondary-400 flex items-center justify-center">
              {initials ? (
                <span className="text-background-50 text-sm font-semibold">{initials}</span>
              ) : (
                <i className="ri-user-3-line text-background-50 text-sm"></i>
              )}
            </div>
          </button>

          {showProfile && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowProfile(false)}></div>
              <div className="absolute top-full right-0 mt-2 w-48 bg-background-50 border border-background-200 rounded-lg shadow-sm z-50 py-1">
                <div className="px-3 py-2 border-b border-background-100">
                  <p className="text-sm font-medium text-foreground-800 truncate">{displayName}</p>
                  <p className="text-xs text-foreground-400 truncate">{restaurant.name}</p>
                </div>
                <button
                  onClick={() => { setShowProfile(false); navigate('/dashboard/settings'); }}
                  className="w-full text-left px-3 py-2 text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  Account Settings
                </button>
                <div className="border-t border-background-100 mt-1 pt-1">
                  <button
                    onClick={handleSignOut}
                    disabled={signingOut}
                    className="w-full text-left px-3 py-2 text-sm text-accent-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-60"
                  >
                    {signingOut ? 'Signing out…' : 'Sign Out'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
